/**
 * App-wide stacking layers. Keep cross-surface ordering here; z-index values
 * used only inside a component's own stacking context can stay local.
 *
 * In particular, elevate portal/root elements rather than only their children:
 * a descendant cannot escape an ancestor stacking context.
 */
export const Z_INDEX = {
    COPY_OVERLAY: 10,
    APP_CONTENT: 10,
    APP_NAVBAR: 40,
    MOBILE_SIDEBAR_BACKDROP: 40,
    MOBILE_SIDEBAR: 50,
    TASK_PAGE_TRAY_CONTEXT: 50,
    PORTAL_SHEET: 50,
    FRAME_STATUS: 80,
    INPUT_BAR: 100,
    QUEUE_HUD: 109,
    TASK_TRAY_ROOT: 190,
    TASK_WORKSPACE_SIDEBAR: 195,
    GLOBAL_BROWSER_DOCK: 196,
    TASK_CREATE_MODAL: 199,
    PORTAL_CONTAINER: 200,
    RELEASE_NOTIFICATION: 201,
    ONBOARDING_MODAL: 300,
    GLOBAL_MODAL: 1_000,
    IMAGE_LIGHTBOX: 10_000,
} as const

export const TASK_DOCKED_PANE_WIDTH = "clamp(24rem, 42vw, 52rem)"

const LAST_VIEWED_KEY = "_code_last_viewed"
let codeStorageScope: { nodeId: string; allowLegacyFallback: boolean } | null = null

export function setCodeStorageScope(nodeId: string, allowLegacyFallback: boolean): void {
    codeStorageScope = { nodeId, allowLegacyFallback }
}

function codeStorageKey(base: string): string {
    return codeStorageScope ? `${base}:${codeStorageScope.nodeId}` : base
}

function getScopedStorageValue(base: string): string | null {
    const scoped = localStorage.getItem(codeStorageKey(base))
    if (scoped || !codeStorageScope?.allowLegacyFallback) return scoped
    return localStorage.getItem(base)
}

export interface LastViewed {
    workspaceId: string
    taskId?: string
}

export function getLastViewed(): LastViewed | null {
    try {
        const data = getScopedStorageValue(LAST_VIEWED_KEY)
        if (!data) return null
        return JSON.parse(data) as LastViewed
    } catch {
        return null
    }
}

export function setLastViewed(data: LastViewed): void {
    localStorage.setItem(codeStorageKey(LAST_VIEWED_KEY), JSON.stringify(data))
}

const WORKSPACE_LAST_VIEWED_KEY = "_code_workspace_last_viewed"

interface WorkspaceLastViewed {
    taskId?: string
}

export function getWorkspaceLastViewed(workspaceId: string): WorkspaceLastViewed | null {
    try {
        const data = getScopedStorageValue(WORKSPACE_LAST_VIEWED_KEY)
        if (!data) return null
        const map = JSON.parse(data) as Record<string, WorkspaceLastViewed>
        return map[workspaceId] ?? null
    } catch {
        return null
    }
}

export function setWorkspaceLastViewed(workspaceId: string, data: WorkspaceLastViewed): void {
    try {
        const existing = getScopedStorageValue(WORKSPACE_LAST_VIEWED_KEY)
        const map: Record<string, WorkspaceLastViewed> = existing ? JSON.parse(existing) : {}
        map[workspaceId] = data
        localStorage.setItem(codeStorageKey(WORKSPACE_LAST_VIEWED_KEY), JSON.stringify(map))
    } catch {
        // Ignore storage errors
    }
}

// ============================================================================
// MCP Server Presets
// ============================================================================

import { siAsana, siAtlassian, siFigma, siIntercom, siLinear, siNotion, siPaypal, siSentry, siSquare, siStripe, siVercel, siWebflow } from "simple-icons"
import type { SimpleIcon } from "simple-icons"

export interface McpPreset {
    id: string
    name: string
    description: string
    transportType: "http" | "stdio"
    url?: string
    command?: string
    args?: string[]
    /** Simple Icon for this preset. Find slugs at https://simpleicons.org */
    icon: SimpleIcon
    docsUrl?: string
}

/**
 * Built-in MCP server presets for popular services.
 * Only includes verified, production-ready servers from major companies.
 * OAuth endpoints are discovered automatically from the server URL per MCP spec.
 *
 * To add a new preset:
 * 1. Find the icon at https://simpleicons.org
 * 2. Import it: import { siIconname } from "simple-icons"
 * 3. Add the preset with the icon
 */
export const MCP_PRESETS: Record<string, McpPreset> = {
    // Productivity & Collaboration
    notion: {
        id: "notion",
        name: "Notion",
        description: "Access pages, databases, and workspace content",
        transportType: "http",
        url: "https://mcp.notion.com/mcp",
        icon: siNotion,
    },
    linear: {
        id: "linear",
        name: "Linear",
        description: "Manage issues, projects, and team workflows",
        transportType: "http",
        url: "https://mcp.linear.app/sse",
        icon: siLinear,
    },
    asana: {
        id: "asana",
        name: "Asana",
        description: "Manage tasks, projects, and team work",
        transportType: "http",
        url: "https://mcp.asana.com/sse",
        icon: siAsana,
    },

    // Development
    vercel: {
        id: "vercel",
        name: "Vercel",
        description: "Manage deployments, domains, and projects",
        transportType: "http",
        url: "https://mcp.vercel.com",
        icon: siVercel,
    },

    // Payments & Finance
    stripe: {
        id: "stripe",
        name: "Stripe",
        description: "Access customers, payments, and billing data",
        transportType: "http",
        url: "https://mcp.stripe.com",
        icon: siStripe,
    },
    paypal: {
        id: "paypal",
        name: "PayPal",
        description: "Access transactions, invoices, and payment data",
        transportType: "http",
        url: "https://mcp.paypal.com/sse",
        icon: siPaypal,
    },
    square: {
        id: "square",
        name: "Square",
        description: "Access payments, inventory, and business data",
        transportType: "http",
        url: "https://mcp.squareup.com/sse",
        icon: siSquare,
    },

    // Design & Content
    webflow: {
        id: "webflow",
        name: "Webflow",
        description: "Manage sites, CMS content, and hosting",
        transportType: "http",
        url: "https://mcp.webflow.com/mcp",
        icon: siWebflow,
    },
    figma: {
        id: "figma",
        name: "Figma",
        description: "Access designs, components, and design systems",
        transportType: "http",
        url: "https://mcp.figma.com/mcp",
        icon: siFigma,
    },

    // Developer Tools
    atlassian: {
        id: "atlassian",
        name: "Atlassian",
        description: "Access Jira issues and Confluence pages",
        transportType: "http",
        url: "https://mcp.atlassian.com/v1/mcp",
        icon: siAtlassian,
    },
    sentry: {
        id: "sentry",
        name: "Sentry",
        description: "Search, query, and debug errors",
        transportType: "http",
        url: "https://mcp.sentry.dev/mcp",
        icon: siSentry,
    },
    // Analytics & Customer
    intercom: {
        id: "intercom",
        name: "Intercom",
        description: "Access customer conversations and data",
        transportType: "http",
        url: "https://mcp.intercom.com/mcp",
        icon: siIntercom,
    },
} as const

export const MCP_PRESET_IDS = Object.keys(MCP_PRESETS) as (keyof typeof MCP_PRESETS)[]

// ============================================================================
// Model Configuration (daemon-owned via Nest provider.list)
// ============================================================================

export {
    providerModelRegistry,
    HARNESS_META,
    DEFAULT_HARNESS_ID,
    DEFAULT_MODEL,
    getModelFullId,
    getModelsForHarness,
    getDefaultModelForHarness,
    supportsSteeringForHarness,
    resolveModelForHarness,
    normalizeModelClass,
    isHarnessId,
    harnessIdForProvider,
    providerIdForHarness,
} from "./modelRegistry"
export type { ModelEntry, HarnessModelConfig } from "./modelRegistry"
